import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import AppShell from '@/components/layout/AppShell';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  TrendingUp, TrendingDown, Users, Calendar, Clock, BarChart3, 
  PieChart, Activity, Target, Loader2, RefreshCw, ArrowUp, ArrowDown,
  Sun, Moon, Building2, CalendarDays, Settings, DollarSign, ShieldCheck,
  Printer, Download, FileText, Pencil
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart
} from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';
import PageHero, { HERO_IMAGES } from '@/components/common/PageHero';
import FeatureGate from '@/components/FeatureGate';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Color palette
const COLORS = {
  primary: '#f59e0b',
  secondary: '#3b82f6',
  success: '#10b981',
  warning: '#f97316',
  danger: '#ef4444',
  purple: '#8b5cf6',
  pink: '#ec4899',
  teal: '#14b8a6'
};

const CHART_COLORS = ['#f59e0b', '#3b82f6', '#10b981', '#f97316', '#8b5cf6', '#ec4899', '#14b8a6', '#ef4444'];

export default function AttendanceTrendsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [timePeriod, setTimePeriod] = useState('30');
  
  // Capacity modal state
  const [showCapacityModal, setShowCapacityModal] = useState(false);
  const [capacityInput, setCapacityInput] = useState('');
  const [savingCapacity, setSavingCapacity] = useState(false);
  
  // Tuition modal state
  const [showTuitionModal, setShowTuitionModal] = useState(false);
  const [tuitionInput, setTuitionInput] = useState('');
  const [savingTuition, setSavingTuition] = useState(false);
  
  // Safety alert fee modal state
  const [safetyFeeInput, setSafetyFeeInput] = useState('');
  
  // Data states
  const [metrics, setMetrics] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [weeklyData, setWeeklyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [yearlyData, setYearlyData] = useState({ data: [], years: [] });
  const [classroomData, setClassroomData] = useState([]);
  const [dayOfWeekData, setDayOfWeekData] = useState([]);
  const [dayOfWeekInsights, setDayOfWeekInsights] = useState({});
  const [timeData, setTimeData] = useState({ data: [], insights: {} });
  const [projections, setProjections] = useState({ nextWeek: [], dayAverages: {} });
  const [seasonalData, setSeasonalData] = useState({ data: [], insights: {} });

  useEffect(() => {
    fetchAllData();
  }, [timePeriod]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const days = parseInt(timePeriod);
      
      const [
        metricsRes,
        dailyRes,
        weeklyRes,
        monthlyRes,
        yearlyRes,
        classroomRes,
        dayOfWeekRes,
        timeRes,
        projectionsRes,
        seasonalRes
      ] = await Promise.all([
        axios.get(`${API}/attendance-analytics/metrics?days=${days}`),
        axios.get(`${API}/attendance-analytics/daily?days=${days}`),
        axios.get(`${API}/attendance-analytics/weekly?weeks=${Math.ceil(days / 7)}`),
        axios.get(`${API}/attendance-analytics/monthly?months=12`),
        axios.get(`${API}/attendance-analytics/yearly`),
        axios.get(`${API}/attendance-analytics/by-classroom?days=${days}`),
        axios.get(`${API}/attendance-analytics/by-day-of-week?days=${days}`),
        axios.get(`${API}/attendance-analytics/by-time?days=${days}`),
        axios.get(`${API}/attendance-analytics/projections`),
        axios.get(`${API}/attendance-analytics/seasonal-trends`)
      ]);

      setMetrics(metricsRes.data);
      setDailyData(dailyRes.data.data || []);
      setWeeklyData(weeklyRes.data.data || []);
      setMonthlyData(monthlyRes.data.data || []);
      setYearlyData(yearlyRes.data);
      setClassroomData(classroomRes.data.data || []);
      setDayOfWeekData(dayOfWeekRes.data.data || []);
      setDayOfWeekInsights(dayOfWeekRes.data.insights || {});
      setTimeData(timeRes.data);
      setProjections(projectionsRes.data);
      setSeasonalData(seasonalRes.data);
      
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load attendance analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  // Handle saving center capacity
  const handleSaveCapacity = async () => {
    const capacity = parseInt(capacityInput);
    if (isNaN(capacity) || capacity < 1) {
      toast.error('Please enter a valid capacity (minimum 1)');
      return;
    }
    
    setSavingCapacity(true);
    try {
      await axios.put(`${API}/center-info`, { centerCapacity: capacity });
      toast.success('Center capacity updated successfully!');
      setShowCapacityModal(false);
      setCapacityInput('');
      // Refresh data to show updated capacity utilization
      await fetchAllData();
    } catch (error) {
      console.error('Error saving capacity:', error);
      toast.error('Failed to save center capacity');
    } finally {
      setSavingCapacity(false);
    }
  };

  // Handle saving average weekly tuition
  const handleSaveTuition = async () => {
    const tuition = parseFloat(tuitionInput);
    if (isNaN(tuition) || tuition < 0) {
      toast.error('Please enter a valid tuition amount');
      return;
    }
    
    setSavingTuition(true);
    try {
      await axios.put(`${API}/center-info`, { avgWeeklyTuition: tuition });
      toast.success('Average weekly tuition updated successfully!');
      setShowTuitionModal(false);
      setTuitionInput('');
      // Refresh data to show updated revenue projection
      await fetchAllData();
    } catch (error) {
      console.error('Error saving tuition:', error);
      toast.error('Failed to save tuition rate');
    } finally {
      setSavingTuition(false);
    }
  };

  // Calculate hypothetical safety alert revenue (local only, not stored)
  const hypotheticalSafetyRevenue = safetyFeeInput && metrics?.enrolledChildren
    ? (parseFloat(safetyFeeInput) * metrics.enrolledChildren).toFixed(0)
    : 0;

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Check if user is owner/co-owner
  const isOwnerOrCoOwner = user?.role?.toLowerCase() === 'owner' || user?.role?.toLowerCase() === 'co-owner';

  // Metric Card Component
  const MetricCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'amber' }) => (
    <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-gray-500 mb-1">{title}</p>
            <p className={`text-2xl font-bold text-${color}-600`}>{value}</p>
            {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-2 rounded-lg bg-${color}-100`}>
            <Icon className={`w-5 h-5 text-${color}-600`} />
          </div>
        </div>
        {trend && (
          <div className={`flex items-center mt-2 text-xs ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
            {trend === 'up' ? <ArrowUp className="w-3 h-3 mr-1" /> : <ArrowDown className="w-3 h-3 mr-1" />}
            {trendValue}
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading attendance analytics...</p>
          </div>
        </div>
      </AppShell>
    );
  }

  // Print report function
  const handlePrint = () => {
    window.print();
  };

  // Download report as CSV
  const handleDownloadCSV = () => {
    try {
      // Prepare data for CSV
      const csvRows = [];
      
      // Header info
      csvRows.push(['Attendance Trends & Analytics Report']);
      csvRows.push([`Generated: ${new Date().toLocaleDateString()}`]);
      csvRows.push([`Time Period: Last ${timePeriod} days`]);
      csvRows.push([]);
      
      // Key Metrics
      csvRows.push(['KEY METRICS']);
      csvRows.push(['Metric', 'Value']);
      csvRows.push(['Average Daily Attendance', metrics?.avgDailyAttendance || 0]);
      csvRows.push(['Attendance Rate', `${metrics?.attendanceRate || 0}%`]);
      csvRows.push(['Capacity Utilization', `${metrics?.capacityUtilization || 0}%`]);
      csvRows.push(['Total Sign-ins', metrics?.totalSignIns || 0]);
      csvRows.push(['Enrolled Children', metrics?.enrolledChildren || 0]);
      csvRows.push(['Center Capacity', metrics?.centerCapacity || 'Not set']);
      csvRows.push([]);
      
      // Daily Data
      if (dailyData && dailyData.length > 0) {
        csvRows.push(['DAILY ATTENDANCE']);
        csvRows.push(['Date', 'Attendance']);
        dailyData.forEach(row => {
          csvRows.push([row.date || row.day, row.attendance || row.count || 0]);
        });
        csvRows.push([]);
      }
      
      // Classroom Data
      if (classroomData && classroomData.length > 0) {
        csvRows.push(['CLASSROOM BREAKDOWN']);
        csvRows.push(['Classroom', 'Count', 'Percentage']);
        classroomData.forEach(row => {
          csvRows.push([row.name || row.classroom, row.value || row.count, `${row.percentage || 0}%`]);
        });
        csvRows.push([]);
      }
      
      // Day of Week Data
      if (dayOfWeekData && dayOfWeekData.length > 0) {
        csvRows.push(['DAY OF WEEK PATTERNS']);
        csvRows.push(['Day', 'Average Attendance']);
        dayOfWeekData.forEach(row => {
          csvRows.push([row.day || row.name, row.avg || row.average || 0]);
        });
      }
      
      // Convert to CSV string
      const csvContent = csvRows.map(row => 
        row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
      ).join('\n');
      
      // Download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `attendance_report_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Report downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download report');
    }
  };

  return (
    <AppShell>
      {/* Hero Banner */}
      <PageHero 
        imageUrl={HERO_IMAGES.analytics}
        title="Attendance Trends & Analytics"
        subtitle="Analyze patterns to plan staffing, budgets, and operations"
        altText="Analytics dashboard with charts"
      />
      
      <FeatureGate feature="attendance_trends">
      
      <div className="relative z-10 p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <BarChart3 className="w-7 h-7 text-amber-500" />
              Attendance Trends & Analytics
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Analyze patterns to plan staffing, budgets, and operations
            </p>
          </div>
          
          <div className="flex items-center gap-3 flex-wrap">
            <Select value={timePeriod} onValueChange={setTimePeriod}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Time Period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last 12 months</SelectItem>
              </SelectContent>
            </Select>
            
            <Button 
              variant="outline" 
              onClick={handleDownloadCSV}
              className="border-green-300 text-green-700 hover:bg-green-50"
              data-testid="download-report-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
            
            <Button 
              variant="outline" 
              onClick={handlePrint}
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
              data-testid="print-report-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            
            <Button 
              variant="outline" 
              onClick={handleRefresh}
              disabled={refreshing}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Key Metrics Cards with Descriptions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Avg Daily Attendance */}
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Avg Daily Attendance</p>
                  <p className="text-2xl font-bold text-amber-600">{metrics?.avgDailyAttendance || 0}</p>
                  <p className="text-xs text-gray-400">unique children per day</p>
                </div>
                <div className="p-2 rounded-lg bg-amber-100">
                  <Users className="w-5 h-5 text-amber-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 border-t pt-2 mt-2">
                Average number of different children who signed in each operating day during this period.
              </p>
            </CardContent>
          </Card>
          
          {/* Attendance Rate */}
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Attendance Rate</p>
                  <p className="text-2xl font-bold text-blue-600">{metrics?.attendanceRate || 0}%</p>
                  <p className="text-xs text-gray-400">of {metrics?.enrolledChildren || 0} enrolled</p>
                </div>
                <div className="p-2 rounded-lg bg-blue-100">
                  <Target className="w-5 h-5 text-blue-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 border-t pt-2 mt-2">
                What percentage of your enrolled children actually attend on an average day. Higher = better engagement.
              </p>
            </CardContent>
          </Card>
          
          {/* Capacity Utilization */}
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Capacity Utilization</p>
                  {metrics?.capacityNotSet ? (
                    <>
                      <p className="text-lg font-bold text-orange-600">Not Set</p>
                      {isOwnerOrCoOwner && (
                        <Button 
                          variant="link" 
                          className="text-xs text-orange-500 p-0 h-auto hover:text-orange-700"
                          onClick={() => setShowCapacityModal(true)}
                          data-testid="set-capacity-btn"
                        >
                          Click to set capacity
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="text-2xl font-bold text-green-600">{metrics?.capacityUtilization || 0}%</p>
                      <p className="text-xs text-gray-400">of {metrics?.totalCapacity || 0} licensed spots</p>
                      {isOwnerOrCoOwner && (
                        <Button 
                          variant="link" 
                          className="text-xs text-gray-400 p-0 h-auto hover:text-gray-600"
                          onClick={() => {
                            setCapacityInput(String(metrics?.totalCapacity || ''));
                            setShowCapacityModal(true);
                          }}
                          data-testid="edit-capacity-btn"
                        >
                          Edit
                        </Button>
                      )}
                    </>
                  )}
                </div>
                <div className="p-2 rounded-lg bg-green-100">
                  <Building2 className="w-5 h-5 text-green-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 border-t pt-2 mt-2">
                {metrics?.capacityNotSet 
                  ? "Your center's licensed capacity hasn't been set. Set it to track capacity utilization."
                  : "How much of your licensed capacity you're using on average. Helps plan for growth or identify underutilization."
                }
              </p>
            </CardContent>
          </Card>
          
          {/* Operating Days */}
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Operating Days</p>
                  <p className="text-2xl font-bold text-purple-600">{metrics?.operatingDays || 0}</p>
                  <p className="text-xs text-gray-400">{metrics?.totalSignIns || metrics?.totalAttendance || 0} total sign-ins</p>
                </div>
                <div className="p-2 rounded-lg bg-purple-100">
                  <CalendarDays className="w-5 h-5 text-purple-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 border-t pt-2 mt-2">
                Number of days your center recorded attendance during this period. Total sign-ins = sum of all daily attendance.
              </p>
            </CardContent>
          </Card>
          
          {/* Projected Monthly Revenue - Only visible to owners/co-owners */}
          {isOwnerOrCoOwner && (
            <Card className="bg-white border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Projected Monthly Revenue</p>
                    {metrics?.tuitionNotSet ? (
                      <>
                        <p className="text-lg font-bold text-orange-600">Not Set</p>
                        <Button 
                          variant="outline"
                          size="sm"
                          className="mt-2 border-orange-500 text-orange-700 hover:bg-orange-50 font-semibold"
                          onClick={() => setShowTuitionModal(true)}
                          data-testid="set-tuition-btn"
                        >
                          <Pencil className="w-3.5 h-3.5 mr-1.5" />
                          Set Weekly Tuition
                        </Button>
                      </>
                    ) : (
                      <>
                        <p className="text-2xl font-bold text-emerald-600">{formatCurrency(metrics?.projectedMonthlyRevenue || 0)}</p>
                        <p className="text-xs text-gray-400">based on ${metrics?.avgWeeklyTuition}/week avg</p>
                        <Button 
                          variant="outline"
                          size="sm"
                          className="mt-2 border-emerald-500 text-emerald-700 hover:bg-emerald-50 font-semibold"
                          onClick={() => {
                            setTuitionInput(String(metrics?.avgWeeklyTuition || ''));
                            setShowTuitionModal(true);
                          }}
                          data-testid="edit-tuition-btn"
                        >
                          <Pencil className="w-3.5 h-3.5 mr-1.5" />
                          Edit Weekly Tuition
                        </Button>
                      </>
                    )}
                  </div>
                  <div className="p-2 rounded-lg bg-emerald-100">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 border-t pt-2 mt-2">
                  {metrics?.tuitionNotSet 
                    ? "Set your average weekly tuition rate to see projected monthly revenue based on current attendance patterns."
                    : "Estimated monthly revenue = average daily attendance × weekly tuition × 4.33 weeks. Useful for budgeting and financial planning."
                  }
                </p>
              </CardContent>
            </Card>
          )}
          
          {/* Safety Alert Revenue Calculator - Only visible to owners/co-owners */}
          {isOwnerOrCoOwner && (
            <Card className="bg-white border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <p className="text-sm text-gray-500 mb-1">Safety Alert Revenue Potential</p>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-gray-500">$</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="5"
                        value={safetyFeeInput}
                        onChange={(e) => setSafetyFeeInput(e.target.value)}
                        className="w-20 h-8 text-sm"
                        data-testid="safety-fee-calculator-input"
                      />
                      <span className="text-xs text-gray-500">/mo per child</span>
                    </div>
                    {safetyFeeInput && parseFloat(safetyFeeInput) > 0 ? (
                      <p className="text-2xl font-bold text-blue-600">{formatCurrency(hypotheticalSafetyRevenue)}</p>
                    ) : (
                      <p className="text-sm text-gray-400">Enter a fee to see potential</p>
                    )}
                    <p className="text-xs text-gray-400">{metrics?.enrolledChildren || 0} enrolled children</p>
                  </div>
                  <div className="p-2 rounded-lg bg-blue-100">
                    <ShieldCheck className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 border-t pt-2 mt-2">
                  Enter a hypothetical monthly fee to see potential revenue if all families subscribe to Pick Up Safety Alerts. Common rates: $3-10/mo.
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Tabs for Different Views */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-amber-50 p-1">
            <TabsTrigger value="overview" className="data-[state=active]:bg-white">
              <Activity className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="time" className="data-[state=active]:bg-white">
              <Calendar className="w-4 h-4 mr-2" />
              Time Trends
            </TabsTrigger>
            <TabsTrigger value="breakdown" className="data-[state=active]:bg-white">
              <PieChart className="w-4 h-4 mr-2" />
              Breakdown
            </TabsTrigger>
            <TabsTrigger value="planning" className="data-[state=active]:bg-white">
              <TrendingUp className="w-4 h-4 mr-2" />
              Planning
            </TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-6">
            {/* Daily Attendance Chart */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-amber-500" />
                  Daily Attendance Trend
                </CardTitle>
                <CardDescription>How many unique children signed in each day</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={dailyData}>
                      <defs>
                        <linearGradient id="colorAttendance" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3}/>
                          <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 11 }}
                        tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip 
                        labelFormatter={(val) => new Date(val).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="total" 
                        stroke={COLORS.primary} 
                        strokeWidth={2}
                        fill="url(#colorAttendance)" 
                        name="Children"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-xs text-gray-500 mt-3 p-2 bg-gray-50 rounded">
                  <strong>What this shows:</strong> The number of unique children who signed in each day. Use this to spot trends - are certain days consistently busier? Look for patterns like lower attendance on Mondays or Fridays.
                </p>
              </CardContent>
            </Card>

            {/* High/Low Days */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <ArrowUp className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-green-700">Highest Attendance Day</p>
                      <p className="text-xl font-bold text-green-800">{metrics?.highestDay?.count || 0} children</p>
                      <p className="text-xs text-green-600">
                        {metrics?.highestDay?.date ? new Date(metrics.highestDay.date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }) : 'N/A'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <ArrowDown className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm text-red-700">Lowest Attendance Day</p>
                      <p className="text-xl font-bold text-red-800">{metrics?.lowestDay?.count || 0} children</p>
                      <p className="text-xs text-red-600">
                        {metrics?.lowestDay?.date ? new Date(metrics.lowestDay.date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }) : 'N/A'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* TIME TRENDS TAB */}
          <TabsContent value="time" className="space-y-6">
            {/* Weekly Comparison */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Weekly Attendance Comparison</CardTitle>
                <CardDescription>Average daily attendance by week</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={weeklyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                      <Bar dataKey="avgDaily" fill={COLORS.secondary} radius={[4, 4, 0, 0]} name="Avg Daily" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-xs text-gray-500 mt-3 p-2 bg-gray-50 rounded">
                  <strong>What this shows:</strong> Average children per day for each week. Compare week-to-week to see if attendance is growing, declining, or staying steady. Great for spotting seasonal patterns.
                </p>
              </CardContent>
            </Card>

            {/* Monthly Trends */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Monthly Trends</CardTitle>
                <CardDescription>Total and average attendance by month</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                      <Legend />
                      <Bar yAxisId="left" dataKey="totalAttendance" fill={COLORS.primary} radius={[4, 4, 0, 0]} name="Total Sign-Ins" />
                      <Line yAxisId="right" type="monotone" dataKey="avgDaily" stroke={COLORS.success} strokeWidth={2} name="Avg Daily" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-xs text-gray-500 mt-3 p-2 bg-gray-50 rounded">
                  <strong>What this shows:</strong> Bars = total sign-ins that month. Line = average children per day. Use this for budget planning - see which months are busiest for staffing needs.
                </p>
              </CardContent>
            </Card>

            {/* Year over Year */}
            {yearlyData.years?.length > 1 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Year-over-Year Comparison</CardTitle>
                  <CardDescription>Compare attendance across years</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={yearlyData.data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                        <Legend />
                        {yearlyData.years?.map((year, idx) => (
                          <Line 
                            key={year}
                            type="monotone" 
                            dataKey={String(year)} 
                            stroke={CHART_COLORS[idx % CHART_COLORS.length]}
                            strokeWidth={2}
                            dot={{ r: 3 }}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-xs text-gray-500 mt-3 p-2 bg-gray-50 rounded">
                    <strong>What this shows:</strong> Compare the same months across different years. Are you growing year-over-year? Did enrollment increase compared to last year?
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* BREAKDOWN TAB */}
          <TabsContent value="breakdown" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* By Day of Week */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Attendance by Day of Week</CardTitle>
                  <CardDescription>
                    {dayOfWeekData.length > 0 && (
                      <span>Busiest: <strong className="text-green-600">{dayOfWeekInsights?.busiestDay}</strong></span>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={dayOfWeekData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="day" tick={{ fontSize: 11 }} width={40} />
                        <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                        <Bar dataKey="avgAttendance" fill={COLORS.primary} radius={[0, 4, 4, 0]} name="Avg Children" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-xs text-gray-500 mt-3 p-2 bg-gray-50 rounded">
                    <strong>What this shows:</strong> Which days of the week have highest/lowest attendance. Use for staffing - schedule more staff on busy days, reduce on slow days.
                  </p>
                </CardContent>
              </Card>

              {/* By Classroom */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Attendance by Program / Age Group</CardTitle>
                  <CardDescription>Full-Time children are grouped by age group. Other children are grouped by enrollment category.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPie>
                        <Pie
                          data={classroomData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="totalAttendance"
                          nameKey="name"
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                          labelLine={false}
                        >
                          {classroomData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                      </RechartsPie>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-xs text-gray-500 mt-3 p-2 bg-gray-50 rounded">
                    <strong>What this shows:</strong> How attendance is distributed across your classrooms. Identify which rooms are busiest for resource allocation.
                  </p>
                </CardContent>
              </Card>

              {/* Peak Times */}
              <Card className="lg:col-span-2">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5 text-amber-500" />
                    Peak Sign-In & Sign-Out Times
                  </CardTitle>
                  <CardDescription>
                    Peak Sign-In: <strong className="text-blue-600">{timeData.insights?.peakSignIn}</strong> | 
                    Peak Sign-Out: <strong className="text-orange-600">{timeData.insights?.peakSignOut}</strong>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={timeData.data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                        <Legend />
                        <Bar dataKey="signIns" fill={COLORS.secondary} radius={[4, 4, 0, 0]} name="Sign-Ins" />
                        <Bar dataKey="signOuts" fill={COLORS.warning} radius={[4, 4, 0, 0]} name="Sign-Outs" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* PLANNING TAB */}
          <TabsContent value="planning" className="space-y-6">
            {/* Next Week Projections */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-amber-500" />
                  Projected Attendance - Next 7 Days
                </CardTitle>
                <CardDescription>Based on historical day-of-week patterns</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-7 gap-2 mb-4">
                  {projections.nextWeek?.map((day, idx) => (
                    <div key={idx} className="text-center p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-xs text-gray-500">{day.dayName}</p>
                      <p className="text-xs text-gray-400">{new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
                      <p className="text-xl font-bold text-amber-700 mt-1">{day.projected}</p>
                      <p className="text-xs text-gray-500">projected</p>
                    </div>
                  ))}
                </div>
                
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800 font-medium mb-2">Day-of-Week Averages (Historical)</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(projections.dayAverages || {}).map(([day, avg]) => (
                      <span key={day} className="px-2 py-1 bg-white rounded text-xs">
                        {day}: <strong>{avg}</strong>
                      </span>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Seasonal Trends */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Seasonal Patterns</CardTitle>
                <CardDescription>
                  {seasonalData.insights?.peakMonths?.length > 0 && (
                    <>
                      Peak months: <strong className="text-green-600">{seasonalData.insights.peakMonths.join(', ')}</strong>
                      {seasonalData.insights?.lowMonths?.length > 0 && (
                        <> | Low months: <strong className="text-orange-600">{seasonalData.insights.lowMonths.join(', ')}</strong></>
                      )}
                    </>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={seasonalData.data}>
                      <defs>
                        <linearGradient id="colorSeasonal" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={COLORS.success} stopOpacity={0.3}/>
                          <stop offset="95%" stopColor={COLORS.success} stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                      <Area 
                        type="monotone" 
                        dataKey="avgDaily" 
                        stroke={COLORS.success} 
                        strokeWidth={2}
                        fill="url(#colorSeasonal)" 
                        name="Avg Daily Attendance"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Planning Tips */}
            <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-amber-800">Planning Insights</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-3 bg-white/80 rounded-lg">
                    <p className="font-medium text-gray-800 mb-1">Staffing Recommendation</p>
                    <p className="text-sm text-gray-600">
                      Based on your average of <strong>{metrics?.avgDailyAttendance}</strong> children/day, 
                      plan for adequate staff coverage especially on peak days.
                    </p>
                  </div>
                  <div className="p-3 bg-white/80 rounded-lg">
                    <p className="font-medium text-gray-800 mb-1">Capacity Insight</p>
                    <p className="text-sm text-gray-600">
                      You're using <strong>{metrics?.capacityUtilization}%</strong> of your total capacity. 
                      {metrics?.capacityUtilization < 70 
                        ? " Consider marketing efforts to increase enrollment." 
                        : metrics?.capacityUtilization > 90 
                          ? " Near capacity - consider waitlist management."
                          : " Healthy utilization rate!"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Capacity Setting Modal */}
      <Dialog open={showCapacityModal} onOpenChange={setShowCapacityModal}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-amber-500" />
              Set Center Capacity
            </DialogTitle>
            <DialogDescription>
              Enter your center's licensed capacity (total number of children you're licensed to have).
              This helps calculate your capacity utilization rate.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="capacity" className="text-sm font-medium">
              Licensed Capacity
            </Label>
            <Input
              id="capacity"
              type="number"
              min="1"
              placeholder="e.g., 50"
              value={capacityInput}
              onChange={(e) => setCapacityInput(e.target.value)}
              className="mt-2"
              data-testid="capacity-input"
            />
            <p className="text-xs text-gray-500 mt-2">
              This is typically the number shown on your childcare license.
            </p>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowCapacityModal(false)}
              disabled={savingCapacity}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveCapacity}
              disabled={savingCapacity || !capacityInput}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="save-capacity-btn"
            >
              {savingCapacity ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Capacity'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Tuition Rate Setting Modal */}
      <Dialog open={showTuitionModal} onOpenChange={setShowTuitionModal}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-500" />
              Set Average Weekly Tuition
            </DialogTitle>
            <DialogDescription>
              Enter your center's average weekly tuition rate per child. This is used to project monthly revenue based on attendance patterns.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="tuition" className="text-sm font-medium">
              Average Weekly Tuition ($)
            </Label>
            <Input
              id="tuition"
              type="number"
              min="0"
              step="0.01"
              placeholder="e.g., 250"
              value={tuitionInput}
              onChange={(e) => setTuitionInput(e.target.value)}
              className="mt-2"
              data-testid="tuition-input"
            />
            <p className="text-xs text-gray-500 mt-2">
              Use your average rate across all age groups and programs (full-time, part-time, etc.)
            </p>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowTuitionModal(false)}
              disabled={savingTuition}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveTuition}
              disabled={savingTuition || !tuitionInput}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="save-tuition-btn"
            >
              {savingTuition ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Tuition Rate'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </FeatureGate>
    </AppShell>
  );
}
